import { approxEqual, type Vec2, vec } from '../math';

import { type EntitySource } from './source';
import { type Similarity, transformSource } from './transform';

/**
 * Resizing one shape along each of its axes independently.
 *
 * ## Why this exists next to `transformSource`, which refuses to
 *
 * `transformSource` takes a `Similarity` — turn, *uniform* resize, shift — and the reason is
 * recorded in its header: a general affine map can stretch one axis, and under one a circle
 * becomes an ellipse while `circle-through` still says *circle*. Nothing in that source can
 * hold the difference, so the transform would have to silently approximate.
 *
 * That argument is about the operation being **closed over every kind of source**, because a
 * selection may hold any of them. It is not an argument about ellipses. `free-ellipse` stores
 * `rx` and `ry` as two separate numbers; changing them independently is exactly representable,
 * with nothing approximated and nothing lost. The same is true of `free-polyline`, which is
 * raw coordinates, and of `image`, which stores a width and a height.
 *
 * So the limit belongs to the *selection transform*, not to the shapes. This is the narrower
 * operation: applied to a known, closed set of sources, it can do what the general one must
 * not. It is what makes an ellipse fit a rectangle exactly rather than merely fit inside it.
 *
 * ## Why refusal is a third answer and not `null`
 *
 * In `transformSource`, `null` means *"derived — nothing to do, it follows by itself"*, and
 * `transformActions` rightly skips it. If refusal used the same value, a source that cannot
 * express the reshape would be **silently skipped** by the same line, and the figure would come
 * out squashed in parts: some entities stretched, some not, and no error anywhere. That is the
 * exact class of failure the codebase is arranged to avoid, so the two are different answers
 * and the caller is obliged to tell them apart.
 *
 * `reshapeActions` treats a single refusal as a refusal of the whole gesture. All or nothing:
 * a figure is either reshaped or left alone.
 */

/**
 * A resize along two named axes.
 *
 * The axes are given rather than assumed to be the world's, because a turned ellipse can only be
 * stretched exactly along **its own**. Stretching it along the world's would need a shear, which
 * `rx`/`ry`/`rotation` cannot express — and the honest answer there is to refuse, which is what
 * `reshapeSource` does.
 */
export interface Reshape {
  /** The point held still. */
  readonly origin: Vec2;
  /** Unit. */
  readonly axisX: Vec2;
  /** Unit, and perpendicular to `axisX`. */
  readonly axisY: Vec2;
  readonly scaleX: number;
  readonly scaleY: number;
}

export type Reshaped =
  | { readonly kind: 'rewritten'; readonly source: EntitySource }
  /** Derived: it has no coordinates, and its parents carry it. */
  | { readonly kind: 'follows' }
  /** It has coordinates of its own that cannot express this map. */
  | { readonly kind: 'refused' };

const FOLLOWS: Reshaped = Object.freeze({ kind: 'follows' });
const REFUSED: Reshaped = Object.freeze({ kind: 'refused' });

/** Where `point` ends up. */
export function applyReshape(reshape: Reshape, point: Vec2): Vec2 {
  const { origin, axisX, axisY, scaleX, scaleY } = reshape;
  const offset = vec.subtract(point, origin);

  const along = vec.dot(offset, axisX) * scaleX;
  const across = vec.dot(offset, axisY) * scaleY;

  return vec.add(origin, vec.add(vec.scale(axisX, along), vec.scale(axisY, across)));
}

/** Whether the two factors are the same, in which case the map is an ordinary similarity. */
export function isUniform(reshape: Reshape): boolean {
  return approxEqual(reshape.scaleX, reshape.scaleY);
}

const ALIGNMENT_EPSILON = 1e-9;

/**
 * How the reshape's axes sit relative to a direction the source stores.
 *
 * `null` when they sit at some other angle, which is the case no source with a stored
 * orientation can express: the result is sheared, and `rx`/`ry`/`rotation` has nowhere to put a
 * shear. Otherwise the two axes of the reshape *are* the two axes of the shape, and the only
 * question left is which factor belongs to which — `swapped` answers it.
 *
 * Measured by the projection rather than by comparing angles, so a half turn compares equal
 * without any modular arithmetic: an ellipse turned by π is the same ellipse.
 */
function alignmentWith(reshape: Reshape, direction: Vec2): { readonly swapped: boolean } | null {
  const along = Math.abs(vec.dot(reshape.axisX, direction));

  if (approxEqual(along, 1, ALIGNMENT_EPSILON)) return { swapped: false };
  if (approxEqual(along, 0, ALIGNMENT_EPSILON)) return { swapped: true };
  return null;
}

/**
 * The source after a per-axis resize.
 *
 * The switch is exhaustive on purpose, exactly as `transformSource`'s is: a construction added
 * later cannot be quietly forgotten here, because the compiler will not accept the file until
 * its case is written. The default that a forgotten case would otherwise fall into — `refused` —
 * is safe, but it would disable the gesture for a whole figure without saying why, and a
 * compile error is a better way to find that out than a bug report.
 */
export function reshapeSource(source: EntitySource, reshape: Reshape): Reshaped {
  const at = (point: Vec2) => applyReshape(reshape, point);
  const uniform = isUniform(reshape);

  /*
   * A uniform reshape *is* a similarity, so anything that can already be scaled can be
   * reshaped by delegating. This is what lets a mixed selection be resized by an edge handle
   * with the shift key held: every source that could take the uniform version still can.
   */
  const asSimilarity = (): Reshaped => {
    if (!uniform) return REFUSED;
    const similarity: Similarity = {
      center: reshape.origin,
      rotation: 0,
      scale: reshape.scaleX,
      translate: vec.ZERO,
    };
    const moved = transformSource(source, similarity);
    return moved === null ? FOLLOWS : { kind: 'rewritten', source: moved };
  };

  switch (source.op) {
    /* ------------------------------------------------ exactly representable, per axis */

    // A point maps exactly under any linear map; there is nothing about it to distort.
    case 'free-point':
      return { kind: 'rewritten', source: { ...source, position: at(source.position) } };

    // Raw coordinates, so any axes and any factors are exact — this is the one source with
    // nothing at all to lose.
    case 'free-polyline':
      return { kind: 'rewritten', source: { ...source, points: source.points.map(at) } };

    /*
     * Two semi-axes stored separately, and a rotation naming the frame they are measured in.
     * Along that frame this is exact and the rotation is untouched; along any other it is a
     * shear, and the honest answer is to refuse.
     *
     * The radii are taken absolute. A factor may be negative — a corner dragged past its own
     * anchor — and a negative radius is not a mirrored ellipse, it is an ellipse whose bounds
     * come out inside-out and which then vanishes from the spatial index. An ellipse is
     * symmetric about both axes, so the absolute value *is* the mirror image.
     */
    case 'free-ellipse': {
      const alignment = alignmentWith(reshape, vec.fromAngle(source.rotation));
      if (alignment === null) return asSimilarity();

      const [forRx, forRy] = alignment.swapped
        ? [reshape.scaleY, reshape.scaleX]
        : [reshape.scaleX, reshape.scaleY];

      return {
        kind: 'rewritten',
        source: {
          ...source,
          center: at(source.center),
          rx: Math.abs(source.rx * forRx),
          ry: Math.abs(source.ry * forRy),
        },
      };
    }

    /*
     * A width and a height in world units, and no rotation to measure them against — so this is
     * exact along the world's axes, either way round, and expressible along no others.
     *
     * Computed by mapping two opposite corners rather than by scaling the two numbers, because
     * that gets the swapped case and the negative case right without either being a branch.
     */
    case 'image': {
      if (alignmentWith(reshape, vec.UNIT_X) === null) return asSimilarity();

      const corner = at(source.position);
      const far = at(vec.vec2(source.position.x + source.width, source.position.y + source.height));

      return {
        kind: 'rewritten',
        source: {
          ...source,
          position: vec.vec2(Math.min(corner.x, far.x), Math.min(corner.y, far.y)),
          width: Math.abs(far.x - corner.x),
          height: Math.abs(far.y - corner.y),
        },
      };
    }

    /* ------------------------------------------- a stored size, so only a uniform resize */

    /*
     * These carry a length or a size but no way to say that it differs by direction. A circle
     * defined by a radius is a circle; stretching one axis would make it an ellipse and leave
     * the source claiming otherwise. Uniform they can do, by delegation; per-axis they refuse.
     */
    case 'circle-radius':
    case 'ellipse':
    case 'solid':
    case 'text':
    case 'measurement':
    case 'variation-table':
    case 'angle-mark':
      return asSimilarity();

    /* ---------------------------------------------------------------------- derived */

    /*
     * No coordinates and no size of their own: recomputed from parents that have already been
     * reshaped, so they follow exactly. `function-graph` and `axes` are in the run for the
     * reason `transformSource` gives — a formula and a range cannot express a resize, and
     * rewriting a user's algebra behind their back is not something a teaching tool should do.
     */
    case 'plane':
    case 'plane-parallel':
    case 'plane-turn':
    case 'plane-free':
    case 'plane-point':
    case 'space':
    case 'dihedral-mark':
    case 'plane-meet':
    case 'plane-foot':
    case 'segment':
    case 'ray':
    case 'line':
    case 'vector':
    case 'solid-figure':
    case 'polyhedron':
    case 'solid-cut':
    case 'solid-vertex':
    case 'solid-rim-point':
    case 'solid-foot':
    case 'midpoint':
    case 'ratio-point':
    case 'segment-midpoint':
    case 'circle-center':
    case 'circle-through':
    case 'circle-3-points':
    case 'arc-3-points':
    case 'arc-center':
    case 'ellipse-foci':
    case 'perpendicular-line':
    case 'perpendicular-bisector':
    case 'parallel-line':
    case 'angle-bisector':
    case 'tangent-line':
    case 'intersection':
    case 'projected-point':
    case 'circumcenter':
    case 'incenter':
    case 'centroid':
    case 'orthocenter':
    case 'polygon':
    case 'regular-polygon':
    case 'reflected-point':
    case 'rotated-point':
    case 'translated-point':
    case 'scaled-point':
    case 'locus-point':
    case 'polygon-boolean':
    case 'function-graph':
    case 'graph-tangent':
    case 'graph-marks':
    case 'axes':
      return FOLLOWS;
  }
}

/**
 * The frame a source must be stretched along, if it insists on one.
 *
 * Only `free-ellipse` does; everything else per-axis is either raw coordinates, which any frame
 * suits, or stored against the world's axes.
 */
export function reshapeFrameOf(source: EntitySource): number {
  return source.op === 'free-ellipse' ? source.rotation : 0;
}

/**
 * Whether a source can be stretched along one axis at a time.
 *
 * What decides whether the gesture is offered at all. Derived by *probing the switch* rather
 * than by keeping a second list of ops, which would be free to drift away from it. The probe is
 * pointedly non-uniform, so a source that can only manage a uniform resize by delegating to
 * `transformSource` answers no — which is the honest answer to "can this be stretched".
 */
export function isReshapeable(source: EntitySource): boolean {
  const rotation = reshapeFrameOf(source);
  const probe: Reshape = {
    origin: vec.ZERO,
    axisX: vec.fromAngle(rotation),
    axisY: vec.fromAngle(rotation + Math.PI / 2),
    scaleX: 2,
    scaleY: 1,
  };
  return reshapeSource(source, probe).kind === 'rewritten';
}
